/**
 * WORLD SCALE - AN INPUT TO THIS LANE, NOT A DECISION OF IT.
 *
 * Round 2 shipped `WORLD_RADIUS_LEAGUES = 8 * FRAME_WIDTH_LEAGUES` with no marker of any
 * kind on the `8`, while the constant beside it carried a BLOCKED UPSTREAM notice. The
 * block protected nothing: every quantity downstream of the blocked constant is a
 * dimensionless ratio and is bit-identical at 24, 36 and 2400 leagues per frame, and the
 * one number that actually contradicted the published world scale was the unmarked `8`.
 * A stated dependency is only load-bearing if the dependent quantity is not
 * scale-invariant, and nobody had checked which of these were.
 *
 * So world scale is now an INPUT with a type, a single named source, and an instrument
 * that fails when this lane's geometry and the published source disagree. It is not a
 * number this lane may choose.
 *
 * THE INSTRUMENT HAS ALREADY EARNED ITS KEEP. It was written against `art-technique`'s
 * 4.6 px/m and, within the same round, FAILED when that lane landed its unit correction -
 * naming both the sentence that moved and the figure that changed (4,400 screens ->
 * 4,830). The values below are the corrected ones. That is the entire point of the file:
 * the disagreement was found by a test rather than by a frame capture eight lanes later.
 *
 * -- THE ONE NAMED SOURCE ----------------------------------------------------
 *
 * `docs/RESOLUTION-AND-WORLD-SCALE.md`, owned by `art-technique`, sections 3 and 4. The
 * values below are a MIRROR of that document and of nothing else. `scale.test.ts` parses
 * the document and fails when the mirror and the source disagree, so adopting a
 * correction upstream is one edit in this file and no edits anywhere else.
 *
 * -- THE ANISOTROPY, WHICH IS NOT COSMETIC -----------------------------------
 *
 * The corrected source publishes TWO pixels-per-metre figures, because the camera is a
 * three-quarter view at theta = 60 degrees: 5.20 px/m across the screen and 4.50 px/m up
 * it. Its own handback to this lane says so in as many words - "the anisotropy is new and
 * it is not cosmetic: a spiral track parametrised by true arc length traverses screen
 * space at different rates depending on heading."
 *
 * The consequence this lane has to absorb, stated rather than absorbed quietly: THE FRAME
 * HAS TWO ASPECT RATIOS AND THEY ARE DIFFERENT NUMBERS.
 *
 *   PIXEL aspect   960/540 = 1.7778 (16:9). What a renderer and a capture see. It is the
 *                  measured figure off all 77 reference JPEGs.
 *   WORLD aspect   123.08 m / 80.00 m = 1.5385. How much WORLD the frame holds in each
 *                  direction, which is the only one geometry may use.
 *
 * Round 2 used 16:9 for both, which is why its sighting radius was 6.75 leagues and is
 * now 7.80: half the frame's shorter dimension is a claim about world extent, not about
 * pixels, and the two stopped being the same number the moment the camera tilted.
 *
 * -- WHAT A LEAGUE IS, WHICH THE SOURCE ASKED FOR TWICE ----------------------
 *
 * Section 4.6 hands this lane back an open question: a 6.9 km sea is about 1.4 NAUTICAL
 * leagues corner to corner, and this lane's track carries tens of thousands of leagues of
 * arc length, so either the sea is bigger, or a Reelin league is not a nautical league, or
 * the track revisits the same water. The answer, stated here rather than left to infer:
 *
 *   A REELIN LEAGUE IS A MAP UNIT, NOT A NAUTICAL LEAGUE. It is defined as one
 *   `leaguesPerFrameWidth` of a screen width, and its value in metres is DERIVED from
 *   this source - `metresPerLeague` in constants.ts - never chosen here. At the corrected
 *   scale it is 5.13 m.
 *
 * The source's third limb is also true and is not an alternative to the second: the track
 * is a spiral and it does coil many times inside the sea - 33 coils at the published size.
 * Both are published as derived outputs so the two lanes read the same numbers.
 */

/**
 * A world scale, as published by whoever owns resolution and metres-per-pixel.
 *
 * Every field is a quantity that lane measures or models. This lane consumes them and
 * contributes none of them.
 */
export interface WorldScale {
  /** Where these numbers came from, exactly. Quoted in failure messages. */
  readonly sourceId: string;
  /**
   * Whether the source has been ruled on. `blocked-upstream` means the numbers are the
   * current published ones and are expected to move; it does NOT license this lane to
   * substitute its own.
   */
  readonly status: 'blocked-upstream' | 'ruled';
  /** Native authoring grid, pixels. */
  readonly nativeWidthPx: number;
  readonly nativeHeightPx: number;
  /** On-screen pixels per world metre ACROSS the screen, at the native grid. */
  readonly pxPerMetreAcross: number;
  /** On-screen pixels per world metre UP the screen. Smaller: the camera is tilted. */
  readonly pxPerMetreUp: number;
  /** Width of the whole sea, metres. The source publishes a square sea. */
  readonly seaDiameterMetres: number;
  /** Screens of sea the source states, for cross-checking our own derivation. */
  readonly publishedScreensOfSea: number;
  /** The source's modelled cozy sailing pace, m/s. Cross-checked against our cruise. */
  readonly cozyPaceMetresPerSecond: number;
}

/**
 * THE PUBLISHED SCALE, MIRRORED. MODELLED upstream; every figure is that document's.
 *
 * docs/RESOLUTION-AND-WORLD-SCALE.md
 *   §3  "RULING: 960x540 native, delivered at 1920x1080. GRAIN 2."  (owner ruling 2026-09-01;
 *       the earlier 640x360 heading is superseded and ledgered in native-supersessions.json)
 *   §4.5 "| px per metre, across screen | 5.20 |"
 *   §4.5 "| px per metre, up screen (theta 60) | 4.50 |"
 *   §4.5 "| cozy sailing pace | 5 knots = 2.572 m/s |"
 *   §4.5 "RECOMMENDED: a 6.9 km square sea, 48 km2, about 4,830 screens."
 */
export const PUBLISHED_WORLD_SCALE: WorldScale = {
  sourceId: 'docs/RESOLUTION-AND-WORLD-SCALE.md §3 RULING + §4.5 package A',
  status: 'ruled',
  nativeWidthPx: 960,
  nativeHeightPx: 540,
  pxPerMetreAcross: 7.8,
  pxPerMetreUp: 6.75,
  seaDiameterMetres: 6900,
  publishedScreensOfSea: 4830,
  cozyPaceMetresPerSecond: 2.572,
};

/** Metres of world the camera holds across, at the published scale. */
export function frameWidthMetres(scale: WorldScale): number {
  return scale.nativeWidthPx / scale.pxPerMetreAcross;
}

/** Metres of world the camera holds top to bottom. Uses the UP figure; see the header. */
export function frameHeightMetres(scale: WorldScale): number {
  return scale.nativeHeightPx / scale.pxPerMetreUp;
}

/**
 * THE ASPECT GEOMETRY MUST USE: how much world the frame holds across, over how much it
 * holds up. Not 16:9 - see `pixelAspect` and the header.
 */
export function worldAspect(scale: WorldScale): number {
  return frameWidthMetres(scale) / frameHeightMetres(scale);
}

/**
 * The frame's shape in PIXELS. What a renderer and a capture probe see, and what the 77
 * reference JPEGs measured. It may never be used to convert a world length.
 */
export function pixelAspect(scale: WorldScale): number {
  return scale.nativeWidthPx / scale.nativeHeightPx;
}

/**
 * How many screen widths across the whole sea is. THE ratio round 2 hard-coded as 16.
 * The published scale puts it near 56, which is why that `8` was a refutation and not a
 * rounding difference.
 */
export function seaDiameterFrameWidths(scale: WorldScale): number {
  return scale.seaDiameterMetres / frameWidthMetres(scale);
}

/** Screens of sea our geometry implies, for comparison with the source's own figure. */
export function screensOfSea(scale: WorldScale): number {
  return seaDiameterFrameWidths(scale) * (scale.seaDiameterMetres / frameHeightMetres(scale));
}
